import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, BehaviorSubject, of } from 'rxjs';
import { tap } from 'rxjs/operators';
import { apiUrl } from 'src/environments/environment';

/**
 * Service to handle secure token management using HttpOnly cookies.
 * This service communicates with the backend to store/retrieve tokens
 * from HttpOnly cookies instead of browser session storage.
 */
@Injectable({
  providedIn: 'root'
})
export class SecureTokenService {
  private isAuthenticatedSubject = new BehaviorSubject<boolean>(false);
  public isAuthenticated$ = this.isAuthenticatedSubject.asObservable();
  private lastStoredAccessToken: string | null = null;
  private lastStoredRefreshToken: string | null = null;

  constructor(private http: HttpClient) {
    // Don't check auth status automatically - let MSAL handle initial auth
  }

  /**
   * Store the access token in HttpOnly cookie via backend API
   * @param accessToken The access token to store
   * @param refreshToken The refresh token to store
   */
  storeTokens(accessToken: string, refreshToken?: string): Observable<any> {
    const normalizedRefreshToken = refreshToken ?? null;

    if (this.lastStoredAccessToken === accessToken && this.lastStoredRefreshToken === normalizedRefreshToken) {
      this.isAuthenticatedSubject.next(true);
      return of({ message: 'Tokens already stored' });
    }

    return this.http.post(`${apiUrl}api/Auth/StoreTokens`, {
      accessToken,
      refreshToken
    }, {
      withCredentials: true // Essential for cookie handling
    }).pipe(
      tap(() => {
        this.lastStoredAccessToken = accessToken;
        this.lastStoredRefreshToken = normalizedRefreshToken;
        this.isAuthenticatedSubject.next(true);
      })
    );
  }

  /**
   * Clear tokens by calling backend to remove HttpOnly cookies
   */
  clearTokens(): Observable<any> {
    return this.http.post(`${apiUrl}api/Auth/Logout`, {}, {
      withCredentials: true
    }).pipe(
      tap(() => {
        this.lastStoredAccessToken = null;
        this.lastStoredRefreshToken = null;
        this.isAuthenticatedSubject.next(false);
      })
    );
  }
}
